import React, { useEffect, useState } from 'react';
import { strings } from '/imports/strings';
import { Dict } from '/imports/types';
import { capitalize, uniqueArray } from '/imports/utils';
import { Icon } from '/imports/ui/Icon';
import { FormField } from '/imports/ui/FormField';
import { Input, InputProps } from '/imports/ui/Input';
import { NumberInput, NumberInputProps } from '/imports/ui/NumberInput';
import { ImageInput, ImageInputProps } from '/imports/ui/ImageInput';
import { Select, SelectProps } from '/imports/ui/Select';
import { PhoneInput } from '/imports/ui/PhoneInput';
import { Checkbox, CheckboxProps } from '/imports/ui/Checkbox';
import { RichSelect, RichSelectProps } from '/imports/ui/RichSelect';
import { TextArea, TextAreaProps } from '/imports/ui/TextArea';
import { FileInput, FileInputProps } from '/imports/ui/FileInput';
import { FilesInput, FilesInputProps } from '/imports/ui/FilesInput';
import { AddressInput, AddressInputProps } from '/imports/ui/AddressInput';
import { getSavedFormForKey, upsertSavedFormForKey } from '/imports/api/savedForms';
import { DateTimeInput, DateTimeInputProps } from '/imports/ui/DateTimeInput';

export type FormInput<T = any> =
  ((setUpdate: (value: React.SetStateAction<Partial<T>>) => void, update: Partial<T>, document?: T, error?: any) => React.ReactNode) |
  {
    type: typeof Input,
    props?: InputProps
  } |
  {
    type: typeof NumberInput,
    props?: NumberInputProps
  } |
  {
    type: typeof Select,
    props?: SelectProps
  } |
  {
    type: typeof PhoneInput,
    props?: InputProps
  } |
  {
    type: typeof Checkbox,
    props?: CheckboxProps
  } |
  {
    type: typeof RichSelect,
    props?: RichSelectProps
  } |
  {
    type: typeof TextArea,
    props?: TextAreaProps
  } |
  {
    type: typeof ImageInput,
    props?: ImageInputProps
  } |
  {
    type: typeof FileInput,
    props?: FileInputProps
  } |
  {
    type: typeof FilesInput,
    props?: FilesInputProps
  } |
  {
    type: typeof DateTimeInput,
    props?: DateTimeInputProps
  } |
  {
    type: typeof AddressInput,
    props?: AddressInputProps
  }

export interface FieldSpec<T> {
  field: keyof T;
  input?: FormInput<T>;
  label?: string;
  asterisk?: boolean;
  optional?: boolean;
  tip?: string;
  className?: string;
  error?: Dict<string[]>;
}

interface FormBuilderProps<T = any> {
  form: (FieldSpec<T> | React.ReactNode)[][];
  document?: T;
  update: Partial<T & { $unset?: string[] }>;
  setUpdate: (value: React.SetStateAction<Partial<T>>) => void;
  error: any;
  cacheKey?: string;
}

export const FormBuilder = <T,>(props: FormBuilderProps<T>) => {
  const { form, document, update, setUpdate, error, cacheKey } = props;
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!cacheKey) return;
    if (!initialized) setInitialized(true);
    if (!cacheKey) return;
    (async () => {
      if (initialized) return;
      const savedForm = await getSavedFormForKey.call(cacheKey);
      savedForm.data && setUpdate({ ...update, ...savedForm.data });
    })();
    (async () => {
      initialized && await upsertSavedFormForKey.call({ key: cacheKey, data: update });
    })();
  }, [update]);
  return (
    <>
      {
        form.map((group, i) => {
          if (!group.filter(Boolean).length) return null;
          return (
            <div key={i} className="field-group">
              {
                group.map((field, j) => {
                  if (!field) return null;
                  const _field = field as FieldSpec<T>;
                  if (_field.field) {
                    const id = `${new String(_field.field)}-input`;
                    const className = _field.className || '';
                    const input = _field.input;
                    const value = update[_field.field] === undefined && !update.$unset?.includes(_field.field as string) ? document?.[_field.field] : update[_field.field];
                    const _error = _field.error || {};
                    if (typeof input === 'function') {
                      return input(setUpdate, update, document, { ..._error, ...error });
                    }
                    if (input?.type === Checkbox) {
                      return (
                        <FormField id={id} key={j} className={`checkbox ${className}`} field={_field.field as string} error={{ ..._error, ...error }}>
                          {
                            React.createElement(Checkbox as any, {
                              id,
                              checked: update[_field.field] === undefined ? !!document?.[_field.field] : !!update[_field.field],
                              onChange: (checked: boolean) => setUpdate({ ...update, [_field.field]: checked }),
                              ...input?.props as any,
                            },
                            <>
                              {update[_field.field] === undefined ? !!document?.[_field.field] : !!update[_field.field]}
                              {_field.label || (strings as any)[capitalize(_field.field as string)]?.() || `_${String(_field.field)}_`}
                              {_field.asterisk && <span>*</span>}
                              {_field.optional && <span className="optional">({strings.Optional()})</span>}
                              {_field.tip && <Icon className="field-tip" icon="help" tip={_field.tip} />}
                            </>
                            )
                          }
                        </FormField>
                      );
                    }
                    else if (input?.type === RichSelect) {
                      return (
                        <FormField id={id} key={j} field={_field.field as string} error={{ ..._error, ...error }} className={className}>
                          <label>
                            {_field.label || (strings as any)[capitalize(_field.field as string)]?.() || `_${String(_field.field)}_`}
                            {_field.asterisk && <span>*</span>}
                            {_field.optional && <span className="optional">({strings.Optional()})</span>}
                            {_field.tip && <Icon className="field-tip" icon="help" tip={_field.tip} />}
                          </label>
                          {
                            React.createElement((input?.type || Input) as any, {
                              id,
                              value,
                              onChange: (value: any) => setUpdate({ ...update, [_field.field]: value }),
                              onClear: () => setUpdate({ ...update, [_field.field]: undefined, $unset: uniqueArray([...(update.$unset || []), _field.field]) }),
                              ...input?.props as any
                            })
                          }
                        </FormField>
                      );
                    }
                    else if (input?.type === DateTimeInput) {
                      return (
                        <FormField id={id} key={j} field={_field.field as string} error={{ ..._error, ...error }} className={className}>
                          <label>
                            {_field.label || (strings as any)[capitalize(_field.field as string)]?.() || `_${String(_field.field)}_`}
                            {_field.asterisk && <span>*</span>}
                            {_field.optional && <span className="optional">({strings.Optional()})</span>}
                            {_field.tip && <Icon className="field-tip" icon="help" tip={_field.tip} />}
                          </label>
                          {
                            React.createElement((input?.type || Input) as any, {
                              id,
                              value,
                              onChange: (value: any) => setUpdate({ ...update, [_field.field]: new Date(value) }),
                              onClear: () => setUpdate({ ...update, [_field.field]: undefined, $unset: uniqueArray([...(update.$unset || []), _field.field]) }),
                              ...input?.props as any
                            })
                          }
                        </FormField>
                      );
                    }
                    else {
                      return (
                        <FormField id={id} key={j} field={_field.field as string} error={{ ..._error, ...error }} className={className}>
                          <label>
                            {_field.label || (strings as any)[capitalize(_field.field as string)]?.() || `_${String(_field.field)}_`}
                            {_field.asterisk && <span>*</span>}
                            {_field.optional && <span className="optional">({strings.Optional()})</span>}
                            {_field.tip && <Icon className="field-tip" icon="help" tip={_field.tip} />}
                          </label>
                          {
                            React.createElement((input?.type || Input) as any, {
                              id,
                              value,
                              onChange: (value: any) => setUpdate({ ...update, [_field.field]: value }),
                              onClear: () => setUpdate({ ...update, [_field.field]: undefined, $unset: uniqueArray([...(update.$unset || []), _field.field]) }),
                              ...input?.props as any
                            })
                          }
                        </FormField>
                      );
                    }
                  }
                  else {
                    return <React.Fragment key={j}>{field as React.ReactNode}</React.Fragment>;
                  }
                })
              }
            </div>
          );
        })
      }
    </>
  );
};